import { marked } from 'marked';

/**
 * Configura o marked para suportar formatação estilo WhatsApp:
 * - *texto* para negrito
 * - _texto_ para itálico
 * - ~texto~ para tachado
 * - ```código``` para código em bloco
 * - `código` para código inline
 * - Listas com * ou -
 * - Listas numeradas
 * - > para citação
 */
export function configureMarkdown() {
  marked.setOptions({
    breaks: true,
    gfm: true,
    pedantic: false,
  });
}

/**
 * Processa texto com formatação estilo WhatsApp antes de passar para o marked
 * 
 * Regras do WhatsApp:
 * - Negrito: *texto* (sem espaços após * inicial e antes do * final)
 * - Itálico: _texto_ (sem espaços após _ inicial e antes do _ final)
 * - Tachado: ~texto~ (sem espaços)
 * - Monoespaçado: ```texto``` (código inline)
 * - Listas com marcadores: * item ou - item
 * - Listas numeradas: 1. item
 * - Citação: > texto
 */
export function preprocessWhatsAppFormat(text: string): string {
  if (!text) return '';

  // Converte \n literal para quebra de linha real
  let processed = text.replace(/\\n/g, '\n');

  // Protege blocos de código (``` multilinha ```) antes de processar
  const codeBlocks: string[] = [];
  // Aceita linguagem opcional após ``` e requer quebra de linha antes do conteúdo
  processed = processed.replace(/```[^\n]*\n[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `⟦CODEBLOCK${codeBlocks.length - 1}⟧`;
  });

  // Protege código inline (```texto```) do WhatsApp antes de processar
  const inlineCodeBlocks: string[] = [];
  processed = processed.replace(/```([^`\n]+?)```/g, (_match, code) => {
    inlineCodeBlocks.push(`\`${code}\``);
    return `⟦INLINECODE${inlineCodeBlocks.length - 1}⟧`;
  });

  // Garante que listas numeradas tenham linha em branco antes (requisito do marked)
  // Se uma linha termina com texto e a próxima começa com "1. ", adiciona quebra
  processed = processed.replace(/([^\n])\n(1\.\s+)/g, '$1\n\n$2');

  // Converte listas numeradas no formato "n1 item\nn2 item" para markdown
  // Detecta padrões como "n1 Texto\nn2 Texto" (formato da API)
  processed = processed.replace(/^n(\d+)\s+(.+)$/gm, '$1. $2');

  // Converte listas com marcadores no formato "* item" ou "- item"
  processed = processed.replace(/^[\*\-]\s+(.+)$/gm, '* $1');

  // *texto* -> negrito (WhatsApp: sem espaço após * inicial e antes do * final)
  // Não deve pegar * no início de linha (listas)
  processed = processed.replace(/(^|[^\*\n])\*([^\s\*][^\*\n]*?[^\s\*])\*(?!\*)/gm, '$1**$2**');
  processed = processed.replace(/(^|[^\*\n])\*([^\s\*])\*(?!\*)/gm, '$1**$2**');

  // _texto_ -> itálico (WhatsApp: sem espaço após _ inicial e antes do _ final)
  processed = processed.replace(/(^|[^_\n])_([^\s_][^_\n]*?[^\s_])_(?!_)/gm, '$1*$2*');
  processed = processed.replace(/(^|[^_\n])_([^\s_])_(?!_)/gm, '$1*$2*');

  // ~texto~ -> tachado (sem espaços)
  processed = processed.replace(/(^|[^~\n])~([^\s~][^~\n]*?[^\s~])~(?!~)/gm, '$1~~$2~~');
  processed = processed.replace(/(^|[^~\n])~([^\s~])~(?!~)/gm, '$1~~$2~~');

  // Restaura código inline
  inlineCodeBlocks.forEach((code, index) => {
    processed = processed.replace(`⟦INLINECODE${index}⟧`, code);
  });

  // Restaura blocos de código
  codeBlocks.forEach((block, index) => {
    processed = processed.replace(`⟦CODEBLOCK${index}⟧`, block);
  });

  return processed;
}

/**
 * Converte um elemento <table> HTML em um bloco monoespaçado com colunas alinhadas para WhatsApp
 */
function convertHtmlTableToWhatsApp(tableHtml: string): string {
  const rowMatches = Array.from(tableHtml.matchAll(/<tr[\s\S]*?<\/tr>/gi));
  if (rowMatches.length === 0) return '';

  type Row = { cells: string[]; isHeader: boolean };
  const rows: Row[] = rowMatches.map((m) => {
    const rowHtml = m[0];
    const cells = Array.from(rowHtml.matchAll(/<(th|td)[^>]*>([\s\S]*?)<\/\1>/gi)).map((cm) => {
      const raw = cm[2]
        .replace(/<br\s*\/>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .trim();
      return raw;
    });
    const isHeader = /<th\b/i.test(rowHtml);
    return { cells, isHeader };
  });

  const numCols = rows.reduce((max, r) => Math.max(max, r.cells.length), 0);
  const colWidths = new Array(numCols).fill(0);
  rows.forEach((r) => {
    for (let i = 0; i < numCols; i++) {
      const val = (r.cells[i] ?? '').trim();
      if (val.length > colWidths[i]) colWidths[i] = val.length;
    }
  });

  const pad = (val: string, width: number) => val.padEnd(width, ' ');
  const sep = ' | ';

  const headerIndex = rows.findIndex((r) => r.isHeader && r.cells.length > 0);
  const lines: string[] = [];

  if (headerIndex >= 0) {
    const h = rows[headerIndex];
    const headerLine = new Array(numCols)
      .fill('')
      .map((_, i) => pad((h.cells[i] ?? '').trim(), colWidths[i]))
      .join(sep);
    lines.push(headerLine);
    const divider = colWidths.map((w) => '-'.repeat(Math.max(3, w))).join(sep);
    lines.push(divider);
  }

  rows.forEach((r, idx) => {
    if (idx === headerIndex) return;
    const line = new Array(numCols)
      .fill('')
      .map((_, i) => pad((r.cells[i] ?? '').trim(), colWidths[i]))
      .join(sep);
    lines.push(line);
  });

  return '```' + '\n' + lines.join('\n') + '\n' + '```';
}

/**
 * Renderiza markdown com suporte a formatação WhatsApp
 */
export function renderMarkdown(content: string): string {
  const preprocessed = preprocessWhatsAppFormat(content);
  return marked(preprocessed) as string;
}

/**
 * Converte HTML markdown de volta para formato WhatsApp
 */
export function htmlToWhatsApp(html: string): string {
  let text = html;
  
  // Converte <pre><code> para bloco de código (antes de processar <code>)
  text = text.replace(/<pre><code(?:\s[^>]*)?>([\s\S]*?)<\/code><\/pre>/gsi, '```\n$1\n```');

  // Converte tabelas HTML em blocos monoespaçados alinhados
  text = text.replace(/<table[\s\S]*?<\/table>/gsi, (tbl) => convertHtmlTableToWhatsApp(tbl));
  
  // Converte <strong> para *texto*
  text = text.replace(/<strong>(.*?)<\/strong>/g, '*$1*');
  
  // Converte <em> para _texto_
  text = text.replace(/<em>(.*?)<\/em>/g, '_$1_');
  
  // Converte <del> para ~texto~
  text = text.replace(/<del>(.*?)<\/del>/g, '~$1~');
  
  // Converte <code> para `texto` (inline)
  text = text.replace(/<code>(.*?)<\/code>/g, '`$1`');
  
  // Converte listas numeradas <ol><li> mantendo a numeração
  text = text.replace(/<ol>([\s\S]*?)<\/ol>/g, (_, content) => {
    let counter = 1;
    const items = content.replace(/<li>(.*?)<\/li>/g, (_: string, itemContent: string) => {
      return `${counter++}. ${itemContent}\n`;
    });
    return '\n' + items;
  });
  
  // Converte listas com marcadores <ul><li>
  text = text.replace(/<ul>([\s\S]*?)<\/ul>/g, (_, content) => {
    const items = content.replace(/<li>(.*?)<\/li>/g, '* $1\n');
    return '\n' + items;
  });
  
  // Converte <blockquote>
  text = text.replace(/<blockquote>(.*?)<\/blockquote>/g, '> $1');
  
  // Remove tags <p> mantendo o conteúdo
  text = text.replace(/<p>(.*?)<\/p>/g, '$1\n');
  
  // Remove tags HTML restantes
  text = text.replace(/<[^>]+>/g, '');
  
  // Decodifica entidades HTML
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  
  // Remove múltiplas quebras de linha consecutivas
  text = text.replace(/\n{3,}/g, '\n\n');
  
  // Remove espaços no início e fim
  text = text.trim();
  
  return text;
}

// Inicializa a configuração
configureMarkdown();

